import React, { useEffect, useState, useRef } from "react";
import "../../styles/Category.scss";
import { Link } from "react-router-dom";
import { tryFetch, IResponse } from "../../helpers/FetchAPI";

interface EventItem {
  id: string;
  event_type: string;
  event_title: string;
  event_slug: string;
  event_image: string;
  event_rating: string;
  all_dates: string[];
  venue: string;
  next_upcoming_date?: string | null;
  is_invite_only?: string | boolean;
}

const Category: React.FC = () => {
  const [groupedEvents, setGroupedEvents] = useState<Record<string, EventItem[]>>({});
  const sliderRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [seeAllMap, setSeeAllMap] = useState<Record<string, boolean>>({});
  const [hasEvents, setHasEvents] = useState<boolean>(true);
  

  const formatDate = (dateString: string): string => {
    if (typeof dateString !== "string") return "Invalid Date";
    const parsedDate = new Date(dateString);
    if (isNaN(parsedDate.getTime())) return dateString;
    return parsedDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const isUpcomingEvent = (event: EventItem): boolean => {
    if (!event.all_dates || event.all_dates.length === 0) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return event.all_dates.some((dateStr) => {
      const localDate = new Date(dateStr);
      localDate.setHours(0, 0, 0, 0);
      return localDate.getTime() >= today.getTime();
    });
  };

  const getNextUpcomingDate = (dates: string[]): string | null => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sortedDates = dates
      .map((d) => new Date(d))
      .filter((d) => !isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());

    const next = sortedDates.find((d) => d.getTime() >= today.getTime());
    return next ? next.toISOString() : null;
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response: IResponse = await tryFetch({
          url: "/events",
          method: "GET",
        });

        if (response && response.is_success && Array.isArray(response.data)) {

        //    const validEvents = (response.data as any[]).filter(
        //   (event: any) => event.status !== "0"
        // );
        const validEvents = (response.data as any[]).filter(
          (event: any) =>
            event.status !== "0" &&
            event.is_invite_only !== "1" &&
            event.is_invite_only !== true
        );

        const allEvents: EventItem[] = validEvents.map((event: any) => {
          const nextDate = getNextUpcomingDate(event.all_dates || []);
          return {
            id: event.id,
            event_type: event.event_type,
            event_title: event.event_title,
            event_slug: event.event_slug,
            event_image: event.event_image,
            event_rating: event.event_rating,
            all_dates: event.all_dates || [],
            venue: event.venue || "TBA",
            next_upcoming_date: nextDate,
            is_invite_only: event.is_invite_only,
          };
        });

          // const allEvents: EventItem[] = (response.data as any[]).map((event: any) => {
          //   const nextDate = getNextUpcomingDate(event.all_dates || []);
          //   return {
          //     id: event.id,
          //     event_type: event.event_type,
          //     event_title: event.event_title,
          //     event_slug: event.event_slug,
          //     event_image: event.event_image || "/assets/event-def.png",
          //     event_rating: event.event_rating || "N/A",
          //     all_dates: event.all_dates || [],
          //     venue: event.venue || "TBA",
          //     next_upcoming_date: nextDate, 
          //   };
          // });

           const upcomingEvents = allEvents.filter(
            (event) => event.next_upcoming_date !== null
          );

          if (upcomingEvents.length === 0) {
            setHasEvents(false);
            setGroupedEvents({});
            return;
          }

          const grouped: Record<string, EventItem[]> = {};
          upcomingEvents.forEach((event) => {
            if (!grouped[event.event_type]) {
              grouped[event.event_type] = [];
            }
            grouped[event.event_type].push(event);
          });

          setGroupedEvents(grouped);
          setHasEvents(true);
        } else {
          console.error("Failed to fetch events:", response?.error || "Unknown error");
          setHasEvents(false);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        setHasEvents(false);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    const newSeeAllMap: Record<string, boolean> = {};
    Object.keys(sliderRefs.current).forEach((eventType) => {
      const container = sliderRefs.current[eventType];
      if (container && container.scrollWidth > container.clientWidth) {
        newSeeAllMap[eventType] = true;
      } else {
        newSeeAllMap[eventType] = false;
      }
    });
    setSeeAllMap(newSeeAllMap);
  }, [groupedEvents]);
  

  if (!hasEvents) {
    return (
      <div className="category-section no-events">
        <p>
          There are currently no upcoming events.  
          Stay tuned — exciting new experiences are coming soon!
        </p>
      </div>
    );
  }

  return (
    <div className="category-section">
      {Object.entries(groupedEvents).map(([eventType, events]) => (
        <div className="category-block" key={eventType}>
          <div className="category-header">
            <h2>{eventType}</h2>
            {seeAllMap[eventType] && (
              <button className="see-all-btn">See All</button>
            )}
          </div>

          <div
            className="event-slider"
            ref={(el) => {
              sliderRefs.current[eventType] = el;
            }}
          >

           {events.map((event, index) => {
              const displayRating =
                event.event_rating?.toUpperCase() === "GP"
                  ? "General Patronage"
                  : event.event_rating || "N/A";

              return (
                // <Link to={`/event/${event.event_slug}`}  state={{ eventId: event.id }} key={event.id} className="event-card">
                <Link
                  to={`/event/${event.event_slug}`}
                  key={event.id}
                  className="event-card"
                >
                  <img src={event.event_image} alt={event.event_title} />
                  <h3>{event.event_title}</h3>
                  <p className="date">
                    {event.next_upcoming_date
                      ? formatDate(event.next_upcoming_date)
                      : "TBA"}
                  </p>
                  <p className="venue">{event.venue}</p>
                  <p className="rating">{displayRating}</p>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Category;
